import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, API } from "@/lib/api";
import { useCompanion } from "@/companion/CompanionContext";
import { isCinematicPath } from "@/lib/cinematicRoutes";

/**
 * CompanionAssetsContext — companion-agnostic asset store.
 *
 * The active CompanionProfile chooses whether assets exist and where they
 * come from (its `avatarKey`). Today only Toto has uploaded assets;
 * future companions (Morrow reference, family, school) can either wire in
 * their own asset key or leave `capabilities.hasAssets = false` and get
 * an empty store cleanly.
 *
 * Historical alias: the old `TotoAssetsContext.jsx` and `useTotoAssets()`
 * hook re-export from here so pre-migration imports keep working.
 */
const Ctx = createContext({ assets: {}, refresh: () => {}, loaded: false });

export function CompanionAssetsProvider({ children }) {
  const [assets, setAssets] = useState({});
  const [loaded, setLoaded] = useState(false);
  const companion = useCompanion();

  const refresh = useCallback(async () => {
    if (!companion.capabilities?.hasAssets) {
      setAssets({});
      setLoaded(true);
      return;
    }
    // Cinematic routes never need companion assets. Skip the fetch so
    // /present and /lab/* stay silent in the network tab.
    if (isCinematicPath(window.location.pathname)) {
      setAssets({});
      setLoaded(true);
      return;
    }
    try {
      const r = await api.get("/toto/assets");
      const map = {};
      for (const a of r.data) {
        map[a.state] = { url: `${API}/toto/assets/${a.state}/content?v=${encodeURIComponent(a.uploaded_at || "")}`, ...a };
      }
      setAssets(map);
    } catch {
      setAssets({});
    } finally {
      setLoaded(true);
    }
  }, [companion.capabilities?.hasAssets]);

  useEffect(() => { refresh(); }, [refresh]);

  return <Ctx.Provider value={{ assets, refresh, loaded }}>{children}</Ctx.Provider>;
}

export const useCompanionAssets = () => useContext(Ctx);
